import { Box, Button, Chip, DialogActions, DialogContent, DialogTitle, Dropdown, FormControl, Input, Menu, MenuButton, MenuItem, Modal, ModalDialog, Table, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WorkOrderDialog from "../Dialogs/WorkOrderDialog";
import ReassignPackingListDialog from "../Dialogs/ReassignPackingListDialog";
import WorkOrderHistoryDialog from "../Dialogs/WorkOrderHistoryDialog";
import LinkToTicketDialog from "../Dialogs/LinkToTicketDialog";
import SignatureCanvas from "react-signature-canvas";
import WrappedButton from "../WrappedButton";

export interface PackingList {
	BuildingName: string;
	InvoiceNumber: string;
	SerialNumber: string;
	Mechanic: string;
	InvoiceHeaderID: string;
	PackingListID: string;
	Notes: string;
	HasSignature: boolean;
	ApprovedDate: string;
	ApprovedDateRaw: Date | null;
	TypeOfWork: string;
	HasTicket: boolean;
	TotalItems: number;
	RemainingItems: number;
	AllItemsCompleted: boolean;
	FiftyPercentPaid: string;
	CompletionDate: string;
	CompletionDateRaw: Date | null;
	TicketNumbers: string;
}

type SortColumn = "BuildingName" | "InvoiceNumber" | "SerialNumber" | "Mechanic" | "ApprovedDate";
type SortDirection = "asc" | "desc";

const fiftyPercentPaidChip = (value: string) => (
	<Chip size="sm" variant="soft" color={value === "Yes" ? "success" : "danger"}>
		{value}
	</Chip>
);

interface ApprovedPackingListsProps {
	showFinished?: boolean;
}

export default function ApprovedPackingLists(props: ApprovedPackingListsProps) {
	const showFinished = props.showFinished ?? false;

	return <AssignedWorkOrdersContent showFinished={showFinished} />;
}

function AssignedWorkOrdersContent(props: { showFinished: boolean }) {
	const [data, setData] = React.useState<Array<PackingList>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [workOrderModal, setWorkOrderModal] = React.useState(false);
	const [currentPackingListId, setCurrentPackingListId] = React.useState<string>("");
	const [currentTypeOfWork, setCurrentTypeOfWork] = React.useState("");
	const [currentNotes, setCurrentNotes] = React.useState("");
	const [reassignModal, setReassignModal] = React.useState(false);
	const [historyModal, setHistoryModal] = React.useState(false);
	const [currentMechanic, setCurrentMechanic] = React.useState("");
	const [currentInvoiceNumber, setCurrentInvoiceNumber] = React.useState("");
	const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
	const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc");
	const [signModal, setSignModal] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [currentBuildingName, setCurrentBuildingName] = React.useState("");
	const [linkToTicketModal, setLinkToTicketModal] = React.useState(false);

	let sigRef: any = null;

	React.useEffect(() => {
		setPageNumber(0);
	}, [props.showFinished]);

	React.useEffect(() => {
		getData();
	}, []);

	const getData = () => {
		fetch("PackingList/GetPackingLists", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ approved: true }),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							BuildingName: y.building_name,
							InvoiceNumber: y.invoice_number,
							SerialNumber: y.serial_number,
							Mechanic: y.mechanic_name,
							InvoiceHeaderID: y.invoice_header_id,
							PackingListID: y.packing_list_id,
							Notes: y.notes ?? "",
							HasSignature: y.has_signature ?? false,
							ApprovedDate: y.approved_date ? new Date(y.approved_date).toLocaleDateString() : "",
							ApprovedDateRaw: y.approved_date ? new Date(y.approved_date) : null,
							TypeOfWork: y.proposal_line_item_type ?? "",
							HasTicket: y.has_ticket ?? false,
							TotalItems: y.total_items ?? 0,
							RemainingItems: y.remaining_items ?? 0,
							AllItemsCompleted: y.all_items_completed ?? false,
							FiftyPercentPaid: y.fifty_percent_paid ?? "No",
							CompletionDate: y.completion_date ? new Date(y.completion_date).toLocaleString() : "",
							CompletionDateRaw: y.completion_date ? new Date(y.completion_date) : null,
							TicketNumbers: y.ticket_numbers ?? "",
						} as PackingList;
					})
				);
			});
	};

	const openWorkOrderDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentBuildingName(inv.BuildingName);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setCurrentTypeOfWork(inv.TypeOfWork);
		setCurrentNotes(inv.Notes);
		setWorkOrderModal(true);
	};

	const openReassignDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentMechanic(inv.Mechanic);
		setReassignModal(true);
	};

	const openHistoryDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setHistoryModal(true);
	};

	const openLinkToTicketDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentBuildingName(inv.BuildingName);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setLinkToTicketModal(true);
	};

	const openSignedPdf = (inv: PackingList) => {
		fetch("PackingList/GetSignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				packingListId: inv.PackingListID,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			});
	};

	const openUnsignedPdf = (inv: PackingList) => {
		fetch("PackingList/GetUnsignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNumber: inv.InvoiceNumber,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + x;
			});
	};

	const openSignDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setCurrentBuildingName(inv.BuildingName);
		setSignModal(true);
	};

	const saveSignature = () => {
		if (sigRef.isEmpty()) {
			alert("A signature is required.");
			return;
		}
		setIsSaving(true);

		var dt = {
			base64Image: sigRef.toDataURL(),
			packingListId: currentPackingListId,
			estimateNumber: currentInvoiceNumber,
			buildingName: currentBuildingName,
		};

		fetch("PackingList/SignApprovedPackingList", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(dt),
		}).then((x) => {
			setSignModal(false);
			setIsSaving(false);
			getData();
		});
	};

	const handleSort = (column: SortColumn) => {
		if (sortColumn === column) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortColumn(column);
			setSortDirection("asc");
		}
	};

	const renderSortIcon = (column: SortColumn) => {
		if (sortColumn !== column) return null;
		return sortDirection === "asc" ? (
			<ArrowUpwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
		) : (
			<ArrowDownwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
		);
	};

	const tabFilteredData = data?.filter((x) => x.AllItemsCompleted === props.showFinished);

	const filteredData = tabFilteredData?.filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber.toString().includes(globalFilter) ||
			x.Mechanic.toLowerCase().includes(globalFilter.toLowerCase())
	);

	const maxPages = Math.max(1, Math.ceil((filteredData ?? []).length / 25));

	const sortedData = filteredData?.sort((a, b) => {
		if (!sortColumn) return 0;
		let comparison = 0;
		if (sortColumn === "ApprovedDate") {
			const dateA = a.ApprovedDateRaw?.getTime() ?? 0;
			const dateB = b.ApprovedDateRaw?.getTime() ?? 0;
			comparison = dateA - dateB;
		} else {
			comparison = a[sortColumn].localeCompare(b[sortColumn]);
		}
		return sortDirection === "asc" ? comparison : -comparison;
	});

	const thisRows = sortedData?.slice(pageNumber * 25, (pageNumber + 1) * 25);

	return (
		<>
			<WorkOrderDialog
				open={workOrderModal}
				packingListId={currentPackingListId}
				clientName={currentBuildingName}
				estimateNumber={currentInvoiceNumber}
				typeOfWork={currentTypeOfWork}
				initialNotes={currentNotes}
				onClose={() => setWorkOrderModal(false)}
				onSaved={() => {
					setWorkOrderModal(false);
					getData();
				}}
			/>
			<ReassignPackingListDialog
				open={reassignModal}
				packingListId={currentPackingListId}
				currentMechanic={currentMechanic}
				onClose={() => setReassignModal(false)}
				onSaved={() => {
					setReassignModal(false);
					getData();
				}}
			/>
			<WorkOrderHistoryDialog
				open={historyModal}
				packingListId={currentPackingListId}
				invoiceNumber={currentInvoiceNumber}
				onClose={() => setHistoryModal(false)}
			/>
			<LinkToTicketDialog
				open={linkToTicketModal}
				packingListId={currentPackingListId}
				buildingName={currentBuildingName}
				estimateNumber={currentInvoiceNumber}
				onClose={() => setLinkToTicketModal(false)}
				onSaved={() => {
					setLinkToTicketModal(false);
					getData();
				}}
			/>
			<Modal open={signModal}>
				<ModalDialog>
					<DialogTitle>Signature</DialogTitle>
					<DialogContent>
						<SignatureCanvas
							ref={(ref: any) => {
								sigRef = ref;
							}}
							penColor="black"
							canvasProps={{ width: "900", height: "400", style: { border: "1px solid black", borderRadius: "8px" } }}
						></SignatureCanvas>
					</DialogContent>
					<DialogActions>
						<WrappedButton onClick={saveSignature} busy={isSaving}>
							Save Signature
						</WrappedButton>
						<Button
							onClick={() => {
								setSignModal(false);
							}}
							color="primary">
							Close
						</Button>
					</DialogActions>
				</ModalDialog>
			</Modal>
			<Box sx={{ overflowY: "auto", padding: 2 }}>
				<div style={{ display: "flex" }}>
					<div style={{ flex: 1 }}>
						<Typography level="title-lg">{props.showFinished ? "Finished Work Orders" : "Pending Assigned Work Orders"}</Typography>
					</div>
					<FormControl sx={{ marginRight: 1 }}>
						<Input
							name="Search"
							placeholder="Global Search"
							onChange={(event) => {
								setGlobalFilter(event.target.value);
								setPageNumber(0);
							}}
							value={globalFilter}
						/>
					</FormControl>
				</div>
				<Table stripe={"odd"} hoverRow>
					<thead>
						<tr>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("BuildingName")}>
								Building Name
								{renderSortIcon("BuildingName")}
							</th>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("InvoiceNumber")}>
								Estimate #
								{renderSortIcon("InvoiceNumber")}
							</th>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("SerialNumber")}>
								Serial #
								{renderSortIcon("SerialNumber")}
							</th>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("Mechanic")}>
								Mechanic
								{renderSortIcon("Mechanic")}
							</th>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("ApprovedDate")}>
								Approved Date
								{renderSortIcon("ApprovedDate")}
							</th>
							<th>50% Paid</th>
							<th>Items</th>
							{props.showFinished && <th>Ticket #</th>}
							{props.showFinished && <th>Completed</th>}
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{thisRows?.map((x, idx) => {
							const assignedCount = x.TotalItems - x.RemainingItems;
							return (
								<tr key={idx} style={{ cursor: "pointer" }}>
									<td>{x.BuildingName}</td>
									<td>{x.InvoiceNumber}</td>
									<td>{x.SerialNumber}</td>
									<td>{x.Mechanic}</td>
									<td>{x.ApprovedDate}</td>
									<td>{fiftyPercentPaidChip(x.FiftyPercentPaid)}</td>
									<td>
										{x.TotalItems > 0 ? `${assignedCount}/${x.TotalItems} assigned` : "—"}
									</td>
									{props.showFinished && <td>{x.TicketNumbers || "—"}</td>}
									{props.showFinished && <td>{x.CompletionDate || "—"}</td>}
									<td>
										<Dropdown>
											<MenuButton variant="plain" size="sm">
												<MoreVertIcon />
											</MenuButton>
											<Menu>
												{!x.HasSignature && <MenuItem onClick={() => openUnsignedPdf(x)}>Open PDF</MenuItem>}
												{!x.HasSignature && <MenuItem onClick={() => openSignDialog(x)}>Sign Packing List</MenuItem>}
												{x.HasSignature && <MenuItem onClick={() => openSignedPdf(x)}>Open Signed PDF</MenuItem>}
												<MenuItem onClick={() => openWorkOrderDialog(x)}>Work Order</MenuItem>
												{!x.AllItemsCompleted && (
													<MenuItem onClick={() => openLinkToTicketDialog(x)}>Link to Ticket</MenuItem>
												)}
												<MenuItem onClick={() => openReassignDialog(x)}>Reassign Tech</MenuItem>
												<MenuItem onClick={() => openHistoryDialog(x)}>View History</MenuItem>
											</Menu>
										</Dropdown>
									</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={props.showFinished ? 10 : 8}>
								<div style={{ float: "right" }}>
									<span>
										<KeyboardArrowLeftIcon
											onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)}
											sx={{ verticalAlign: "middle", cursor: "pointer" }}
										/>{" "}
										{`Page ${pageNumber + 1} of ${maxPages}`}{" "}
										<KeyboardArrowRightIcon
											onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)}
											sx={{ verticalAlign: "middle", cursor: "pointer" }}
										/>
									</span>
								</div>
							</td>
						</tr>
					</tfoot>
				</Table>
			</Box>
		</>
	);
}
